import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import {
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  AccordionDetails as MuiAccordionDetails,
  Grid,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
} from '@material-ui/core';
import {
  IconDotsVertical,
  IconQuestionMark,
  IconMessageCircle,
  IconTrash,
  IconTag,
  IconAlertTriangle,
  IconBrandWhatsapp,
} from '@tabler/icons';
import { format, formatDistanceStrict } from 'date-fns';
import { es } from 'date-fns/locale';
import clsx from 'clsx';

import { replacePathParams } from '#root/utils';
import { useDialog, useAuth } from '#root/hooks';
import { shopperService } from '#root/services';
import {
  TravelerCard,
  ChatDrawer,
  ExpirationDays,
  GuveryExpressDialog,
  ExpressDetailsDialog,
  ShopperShippingDialog,
} from '#root/components';
import {
  Badge,
  Button as DsButton,
  Card,
  Notice,
  PricePair,
  PriceTotal,
} from '#root/components/ui';
import { AlertDialog } from '#root/ui-components';
import { GuveryPaper } from '#root/ui-components/cards';
import { ArrowDownwardIcon } from '#root/icons';
import { IconBolt } from '@tabler/icons';
import { USER_ORDERS_BY_ID } from '#root/constants/navigation';
import { OrderContent, OrderHeader, OrderProduct } from '../OrderLayout';
import PriceDialog from '../PriceDialog';
import styles from './orderCard.module.scss';

export const OrderStatus = {
  creado: 'CREADO',
  aprobado: 'APROBADO',
  observado: 'OBSERVADO',
  publicado: 'PUBLICADO',
  rePublicado: 'REPUBLICADO',
  conOferta: 'CON_OFERTA',
} as const;

export const OfferStatus = {
  creado: '0',
  negociando: '1',
  aceptado: '2',
  rechazado: '4',
  rechazado_viajero: '5',
  rechazado_comprador: '6',
  rechazado_admin: '7',
} as const;

const Accordion = withStyles({
  root: {
    borderTop: '1px solid rgba(0, 0, 0, .125)',
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 'auto',
    },
  },
  expanded: {},
})(MuiAccordion);

const AccordionSummary = withStyles({
  root: {
    backgroundColor: '#bff6ed',
    borderBottom: '1px solid rgba(0, 0, 0, .125)',
    marginBottom: -1,
    minHeight: 56,
    '&$expanded': {
      minHeight: 56,
    },
  },
  content: {
    '&$expanded': {
      margin: '12px 0',
    },
  },
  expanded: {},
})(MuiAccordionSummary);

const AccordionSummaryObservation = withStyles({
  root: {
    backgroundColor: '#fff4e5',
    borderBottom: '1px solid rgba(0, 0, 0, .125)',
    marginBottom: -1,
    minHeight: 56,
    '&$expanded': {
      minHeight: 56,
    },
  },
  content: {
    '&$expanded': {
      margin: '12px 0',
    },
  },
  expanded: {},
})(MuiAccordionSummary);

const SummaryExpress = withStyles({
  root: {
    color: 'var(--color-express-fee)',
    backgroundColor: '#333;',
    borderBottom: '1px solid rgba(0, 0, 0, .125)',
    marginBottom: -1,
    minHeight: 56,
    '&$expanded': {
      minHeight: 56,
    },
  },
  content: {
    display: 'flex',
    gap: 2,
    justifyContent: 'space-between',
    alignItems: 'center',
    '&$expanded': {
      margin: '12px 0',
    },
  },
  expanded: {},
})(MuiAccordionSummary);

const AccordionDetails = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiAccordionDetails);

const getTime = (orderDate) => {
  let time;
  try {
    time = orderDate
      ? formatDistanceStrict(new Date(orderDate.replace(/ /g, 'T')), new Date(), { locale: es })
      : '---';
  } catch (error) {
    time = 'error';
  }
  return time;
};

const formatTime = (date: string) => {
  if (!date) return;

  let time: string | null;

  try {
    time = format(new Date(date.replace(/ /g, 'T')), "dd 'de' MMM 'del' YYY", { locale: es });
  } catch (error) {
    time = '---';
  }
  return time;
};

const commissionGuvery = (price, com, guv, guvTope, guvExtra) => {
  const sumPrices = Number(price) + Number(com);
  const getPercent = (sumPrices * Number(guv)) / 100;
  const getMaxPercent = getPercent > Number(guvTope) ? Number(guvTope) : getPercent;
  const commission = getMaxPercent + Number(guvExtra);

  return commission;
};

const OrderCard = (props) => {
  const {
    order,
    state,
    hideOffers = false,
    handleLoading,
    createMessage,
    waitingTime,
    handleOpenOfferDrawer,
    setReviewTravelerData,
    travelerReviewsDialog
  } = props;
  const { negocio } = useAuth();
  const history = useHistory();
  const alertDialog = useDialog();
  const priceDialog = useDialog();
  const chatDrawer = useDialog();
  const guveryExpressDialog = useDialog();
  const expressDetailsDialog = useDialog();
  const alertRemoveCourier = useDialog();
  const shippingDialog = useDialog();
  const [priceData, setPriceData] = useState({
    image: '',
    name: '',
    link: '',
    can: null,
    price: null,
    comTraveler: null,
    comGuvery: null,
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const imagePath = order?.img_path;
  const isWaitingApproval =
    imagePath &&
    (order?.estado === OrderStatus.aprobado || order?.estado === OrderStatus.creado);
  const isPublished =
    order?.estado === OrderStatus.publicado || order?.estado === OrderStatus.conOferta;
  const isActive =
    order?.estado === OrderStatus.creado ||
    order?.estado === OrderStatus.aprobado ||
    order?.estado === OrderStatus.observado ||
    order?.estado === OrderStatus.publicado ||
    order?.estado === OrderStatus.rePublicado ||
    order?.estado === OrderStatus.conOferta;
  const quantityOffers = order?.offers?.length;
  const isCourier = Number(order?.flag_envio) === 1;
  const isObserved = order?.admin_motivo && order?.estado === OrderStatus.observado;

  const isExpress = order?.flujo === 'EXPRESS';
  const orderExpress = order?.order_express;
  const orderExpressIsAvailable = orderExpress && order?.order_express?.is_available;
  const orderExpressIsPaying = orderExpress && order?.order_express?.is_paying;
  const orderExpressIsPaid = orderExpress && order?.order_express?.is_paid;
  const orderExpressIsRejected = orderExpress && order?.order_express?.is_rejected;

  const expressHideOffers = (orderExpressIsPaying || orderExpressIsPaid);

  // Pestaña de inactivos (hideOffers): un pedido vencido puede seguir en CREADO/APROBADO/PUBLICADO;
  // no lleva avisos de aprobación ni oferta Express, ni se puede expirar otra vez
  const isInactiveTab = hideOffers;
  // Franja única: aprobación y, si aplica, la oferta de Guvery Express
  const showApproval =
    !isInactiveTab && isWaitingApproval && (!isExpress || orderExpressIsRejected);
  const showExpressOffer =
    !isInactiveTab && (isWaitingApproval || isPublished) && !quantityOffers && !isExpress;
  // Express pagando o pagado: sin observación, chips ni precio referencial (va el total Express)
  const hideDetails = orderExpressIsPaying || orderExpressIsPaid;
  const expirationDays = orderExpressIsAvailable
    ? orderExpress?.expiration_days ?? undefined
    : hideDetails
      ? undefined
      : order?.fixed_expiration_days;
  const canExpire = isActive && !isInactiveTab;
  const hasMenu = isCourier || canExpire;
  const orderDetailPath = replacePathParams(USER_ORDERS_BY_ID, { idp: order?.idp });

  const expressDialogConfirmAction = () => {
    const orderDetailPath = replacePathParams(USER_ORDERS_BY_ID, { idp: order?.idp });
    history.push(orderDetailPath);
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const openOrderDrawer = (offer) => {
    handleOpenOfferDrawer(order, offer);
  };

  const openExpireAlert = () => {
    handleClose();
    alertDialog.openDialog();
  };

  const openRemoveCouriert = () => {
    handleClose();
    alertRemoveCourier.openDialog();
  };

  const handleConfirmExpiring = () => {
    fetchExpiring();
    alertDialog.closeDialog();
  };

  const handleRemoveCourier = () => {
    fetchRemoveCourier();
    alertRemoveCourier.closeDialog();
  };

  const fetchExpiring = async () => {
    const res = await shopperService.orderExpiring(order.idp);
    if (res.error) {
      createMessage.error('No se pudo completar la acción');
      return;
    }
    createMessage.success('Pedido expirado.');
    handleLoading();
  };

  const fetchRemoveCourier = async () => {
    const res = await shopperService.orderRemoveCourier(order.idp);
    if (res.error) {
      createMessage.error('No se pudo completar la acción');
      return;
    }
    createMessage.success('Pedido actualizado.');
    handleLoading();
  };

  const handlePriceDialogOpen = (can, priceOffer, comTraveler, comGuvery) => {
    setPriceData({
      image: imagePath,
      name: order.tit,
      link: order.url,
      can: can,
      price: priceOffer,
      comTraveler: comTraveler,
      comGuvery: comGuvery,
    });
    priceDialog.openDialog();
  };

  return (
    <Card noHover>
      <OrderContent>
        <OrderHeader
          number={order.id_public}
          badges={
            <>
              {hideDetails && (
                <div className={styles.badgeExpress}>
                  Express <IconBolt size={16} />
                </div>
              )}
              {state && (
                <Badge variant="error" size="sm">
                  {state}
                </Badge>
              )}
              {isCourier && (
                <Badge
                  variant="shipping"
                  size="sm"
                  onClick={shippingDialog.openDialog}
                  icon={<IconQuestionMark size={14} />}>
                  Envío Nacional
                </Badge>
              )}
            </>
          }
          meta={
            <>
              <span>Hace {getTime(order.feccre)}</span>
              {expirationDays != null && (
                // ExpirationDays calcula su texto al montar: remontar si cambia el pedido o el origen
                <ExpirationDays
                  key={`${order?.idp}-${orderExpressIsAvailable ? 'express' : 'order'}`}
                  expirationDays={expirationDays}
                />
              )}
            </>
          }
          menu={
            hasMenu && (
              <>
                <IconButton
                  size="small"
                  color="secondary"
                  aria-label="Más acciones"
                  onClick={handleClick}>
                  <IconDotsVertical size={20} />
                </IconButton>
                <Menu
                  id="tools-menu"
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}>
                  {isCourier && (
                    <MenuItem onClick={openRemoveCouriert}>
                      <ListItemIcon style={{ minWidth: 32, color: 'var(--color-status-alert)' }}>
                        <IconTag size={20} />
                      </ListItemIcon>
                      <h6>Quitar envío nacional</h6>
                    </MenuItem>
                  )}
                  {canExpire && (
                    <MenuItem onClick={openExpireAlert}>
                      <ListItemIcon style={{ minWidth: 32, color: 'var(--color-status-error)' }}>
                        <IconTrash size={20} />
                      </ListItemIcon>
                      <h6 className="guv-text-error">Expirar Pedido</h6>
                    </MenuItem>
                  )}
                </Menu>
              </>
            )
          }
        />
        <OrderProduct
          title={order?.tit}
          url={order?.url}
          host={order?.url_host}
          image={imagePath}
          quantity={hideDetails ? undefined : order?.can}
          detail={hideDetails ? undefined : order?.obs}
          chips={hideDetails ? [] : [order?.condicion, order?.empaque]}
          price={
            hideDetails ? (
              // Express pagando o pagado: el total Express abre su detalle
              <PriceTotal
                label="Total Express"
                amount={orderExpress?.total_price}
                aria-label="Ver detalle del total Express"
                onClick={expressDetailsDialog.openDialog}
              />
            ) : (
              order?.pre && (
                <PricePair
                  priceLabel="Precio ref."
                  price={order.pre}
                  commissionLabel="Comisión prop."
                  commission={order.com}
                />
              )
            )
          }
          action={
            orderExpressIsAvailable ? (
              <DsButton size="sm" icon={<IconBolt size={16} />} to={orderDetailPath}>
                Ver / Pagar pedido
              </DsButton>
            ) : (
              <DsButton variant="outline" size="sm" to={orderDetailPath}>
                Ver detalles
              </DsButton>
            )
          }
        />
      </OrderContent>

      {!isInactiveTab && !imagePath && !isPublished && (
        <Notice tone="error" strip>
          <p>
            <strong>Subir imagen:</strong> ingresa al detalle de este pedido y sube la imagen para
            que sea publicado.
          </p>
        </Notice>
      )}

      {isExpress && orderExpress && orderExpressIsAvailable && (
        <SummaryExpress>
          <div style={{display: 'flex'}}>
            <div style={{minWidth: 20, marginRight: 6}}>
              <IconBolt />
            </div>
            <h5 style={{color: '#fff'}}>
              Pedido Express listo! Págalo y recíbelo en el menor tiempo garantizado.
              {' '} <strong style={{color: 'var(--color-express-fee)'}}>Total: $ {orderExpress.total_price}</strong>
            </h5>
          </div>
          <Button variant="outlined" color="inherit" onClick={expressDetailsDialog.openDialog}>
            Detalles
          </Button>
          <ExpressDetailsDialog
        open={expressDetailsDialog.open}
        onClose={expressDetailsDialog.closeDialog}
        order={order}
        orderExpress={orderExpress}
        confirmAction={expressDialogConfirmAction} 
        textAction="Ver Pagar"
      />
        </SummaryExpress>
      )}
      
      {!isInactiveTab && isWaitingApproval && isExpress && !orderExpress && (
        <Notice tone="info" strip icon={<IconBolt size={20} />}>
          Esperando cotización express o la aprobación de Guvery.
        </Notice>
      )}

      {/* Una sola franja: la aprobación y, si aplica, la oferta de Guvery Express */}
      {(showApproval || showExpressOffer) && (
        <Notice
          tone="info"
          strip
          icon={showApproval ? undefined : <IconBolt size={20} />}
          action={
            showExpressOffer && (
              <DsButton
                variant="link"
                size="sm"
                icon={<IconBolt size={16} />}
                onClick={guveryExpressDialog.openDialog}>
                Revisar Guvery Express
              </DsButton>
            )
          }>
          {showApproval && <p>Esperando la aprobación de Guvery ({waitingTime} horas aprox.).</p>}
          {showExpressOffer && (
            <p>¿Necesitas agilizar la entrega? Podrías calificar para Guvery Express.</p>
          )}
        </Notice>
      )}
      {orderExpressIsPaying && (
        <Notice tone="info" strip>
          Validando el pago por parte de Guvery...
        </Notice>
      )}
      {orderExpressIsPaid && (
        <Notice tone="success" strip>
          Asignando tu pedido al mejor viajero...
        </Notice>
      )}

      {isObserved && (
        <Accordion>
          <AccordionSummaryObservation
            expandIcon={<ArrowDownwardIcon />}
            aria-controls="panel-offer"
            id="panel-observation">
            <div className={styles.reazon}>
              <IconAlertTriangle />
              <span className={styles.text}>
                Pedido observado: <strong>{order?.admin_motivo_details?.title}</strong>
              </span>
            </div>
          </AccordionSummaryObservation>
          <AccordionDetails>
            <div className={styles.observationInfo}>
              <p className={styles.hint}>{order?.admin_motivo_details?.message}</p>
              <p className={styles.expInfo}>
                Si no tenemos respuesta hasta el{' '}
                {formatTime(order?.admin_motivo_details?.expiration_date)}, tu pedido será
                expirado.
              </p>
              <div className={styles.solution}>
                <div className={styles.text}>
                  <p>{order?.admin_motivo_details?.solution}</p>
                  <p className={styles.code}>
                    Código de la observación: {order?.admin_motivo_details?.code}
                  </p>
                </div>
                <div className={styles.action}>
                  <Button
                    fullWidth
                    component="a"
                    href={`https://api.whatsapp.com/send?phone=51960684330&text=Hola%20Guvery,%20${order?.admin_motivo_details?.code}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    color="inherit"
                    startIcon={<IconBrandWhatsapp />}>
                    Whatsapp Guvery
                  </Button>
                </div>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
      )}
      {!hideOffers && !expressHideOffers && isPublished && (
        <Accordion>
          <AccordionSummary
            expandIcon={<ArrowDownwardIcon />}
            aria-controls="panel-offer"
            id="panel-offer">
            <h5 style={{color: (quantityOffers < 5) ? 'inherit' : ((quantityOffers === 5) ? 'var(--color-warning)' : 'var(--color-status-error-expired)')}}>
              Tienes <strong>{quantityOffers}</strong> ofertas <strong>(revísalas)</strong>
              {quantityOffers <= 0 && ', aún no se ha hecho ningún acuerdo'}
            </h5>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={3}>
              {quantityOffers > 0 ? (
                <>
                  {quantityOffers < 3 && (
                    <Grid item xs={12}>
                      <div className={styles.hintOffers}>
                        <p>
                          <strong>Consejo:</strong>&nbsp; Los viajeros invierten su tiempo en
                          hacer ofertas. Mantén su motivación elevada aceptando rápido 1 oferta.
                        </p>
                      </div>
                    </Grid>
                  )}
                  {quantityOffers === 3 && (
                    <Grid item xs={12}>
                      <div className={styles.hintOffers}>
                        <p>
                          <strong>Consejo:</strong>&nbsp; Usualmente la oferta más atractiva se
                          encuentra dentro de las primeras 3 que ha recibido tu pedido. Acepta 1
                          oferta rápido.
                        </p>
                      </div>
                    </Grid>
                  )}
                  {quantityOffers === 4 && (
                    <Grid item xs={12}>
                      <div className={styles.hintOffers}>
                        <p>
                          <strong>Consejo:</strong>&nbsp; Los pedidos que reciben más de 4
                          ofertas, se vuelven menos atractivos. Acepta 1 oferta rápido.
                        </p>
                      </div>
                    </Grid>
                  )}
                  {quantityOffers === 5 && (
                    <Grid item xs={12}>
                      <div className={clsx(styles.hintOffers, styles.warning)}>
                        <p>
                          <strong>Importante:</strong>&nbsp; Tienes muchas ofertas, debes aceptar 1
                          pronto pues algunas vencerán y los viajeros llenarán su equipaje.
                          <br />
                          <span className={styles.small}>Si tienes dudas, puedes conversar con el viajero usando el chat.</span>
                        </p>
                      </div>
                    </Grid>
                  )}
                  {quantityOffers >= 6 && (
                    <Grid item xs={12}>
                      <div className={clsx(styles.hintOffers, styles.danger)}>
                        <p>
                          <strong>Acción requerida:</strong>&nbsp; Llegaste al máximo de ofertas que puedes recibir. Acepta 1 o contáctate con Guvery para recibir ayuda.
                          <br />
                          <span className={styles.small}>Recibir muchas ofertas y no aceptar ninguna es una conducta inusual y poco ética.</span>
                        </p>
                      </div>
                    </Grid>
                  )}
                  {order?.offers?.map((offer, index) => {
                    const comGuveryPCT = Number(negocio?.com_porc);
                    const priceOffer = Number(offer.prerea);
                    const commisonOffer = Number(offer?.comvia);
                    const sumPrice = priceOffer + commisonOffer;
                    const commission = commissionGuvery(
                      priceOffer,
                      commisonOffer,
                      comGuveryPCT,
                      negocio?.com_tope,
                      negocio?.com_link,
                    );
                    const price = sumPrice + commission;
                    const isOfferNegotiating =
                      offer.est === OfferStatus.negociando ? true : false;
                    const isOfferAccepted = offer.est === OfferStatus.aceptado ? true : false;
                    const isOfferRejected =
                      offer.est === OfferStatus.rechazado ||
                      offer.est === OfferStatus.rechazado_viajero ||
                      offer.est === OfferStatus.rechazado_comprador ||
                      offer.est === OfferStatus.rechazado_admin
                        ? true
                        : false;
                    const isOfferExpired = offer?.fixed_expiration_days !== undefined && offer.fixed_expiration_days < 0;

                    return (
                      <Grid key={index} item xs={12} sm={6} md={4}>
                        <GuveryPaper disableHover disableBorder className={styles.userOffer}>
                          <div className={styles.header}>
                            <TravelerCard traveler={offer?.traveler} travelerReviewsDialog={travelerReviewsDialog} setReviewTravelerData={setReviewTravelerData} />
                            
                            {(offer.est === OfferStatus.negociando ||
                              offer.est === OfferStatus.aceptado) && (
                              <>
                                <Tooltip arrow title="Chatear" placement="top">
                                  <IconButton
                                    size="small"
                                    color={isOfferExpired ? 'secondary' : 'primary'}
                                    disabled={isOfferExpired ? true : false}
                                    className={
                                      isOfferExpired ? null : 'iconMessageCircleContained'
                                    }
                                    onClick={chatDrawer.openDialog}>
                                    <IconMessageCircle />
                                  </IconButton>
                                </Tooltip>
                                <ChatDrawer
                                  extraTitle={`Oferta #${offer?.id_public}`}
                                  offerId={offer?.ido}
                                  open={chatDrawer.open}
                                  onClose={chatDrawer.closeDialog}
                                />
                              </>
                            )}
                          </div>
                          <div className={styles.content}>
                            {(isOfferNegotiating || isOfferAccepted) && (
                              <div className={styles.statusLabel}>
                                <span>{offer?.status_label}</span>
                              </div>
                            )}
                            <div className={styles.offerInfo}>
                              <h6>
                                Entrega: <strong>{offer.fixed_delivery_date} </strong>
                              </h6>
                              <h6>
                                En: <strong>{offer.lugent}</strong>
                              </h6>
                            </div>
                            <div
                              className={styles.offerPrice}
                              onClick={() =>
                                handlePriceDialogOpen(
                                  offer?.can,
                                  priceOffer,
                                  commisonOffer,
                                  commission,
                                )
                              }>
                              <div className={styles.priceInfo}>
                                <h3>
                                  <span className={styles.currency}>$</span>
                                  <span className={styles.priceTotal}>{price}</span>
                                  <span className={styles.pricetext}> Total a pagar</span>
                                  <span className={styles.offerQty}>
                                    {' '}
                                    &nbsp;x {offer?.can ? offer.can : '1'}
                                  </span>
                                </h3>
                              </div>
                              <Tooltip arrow title="Detalle del total" placement="top">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  className={styles.link}>
                                  <IconQuestionMark />
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>

                          <div className={styles.offerAction}>
                            {!isOfferRejected && (
                              <Button
                                fullWidth
                                disableElevation
                                variant={isOfferExpired ? 'outlined' : 'contained'}
                                color={isOfferExpired ? 'secondary' : 'primary'}
                                onClick={() => openOrderDrawer(offer)}
                                startIcon={isOfferExpired ? null : <IconMessageCircle />}>
                                {isOfferExpired || isOfferAccepted
                                  ? 'Ver oferta'
                                  : 'Ver / Aceptar oferta'}
                              </Button>
                            )}
                            <div className={styles.afterAction}>
                              {!isOfferRejected && (
                                <div className={styles.expirationStatus}>
                                  <ExpirationDays
                                    expirationDays={offer?.fixed_expiration_days}
                                  />
                                </div>
                              )}
                              {isOfferRejected && (
                                <span className={styles.offerRejected}>Rechazado</span>
                              )}
                            </div>
                          </div>
                        </GuveryPaper>
                      </Grid>
                    );
                  })}
                </>
              ) : (
                <Grid item xs={12}>
                  <h4
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}>
                    Sin ofertas aún.
                  </h4>
                </Grid>
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      )}

      {showExpressOffer && (
        <GuveryExpressDialog
          open={guveryExpressDialog.open}
          onClose={guveryExpressDialog.closeDialog}
          order={order}
        />
      )}
      {hideDetails && (
        <ExpressDetailsDialog
          open={expressDetailsDialog.open}
          onClose={expressDetailsDialog.closeDialog}
          order={order}
          orderExpress={orderExpress}
        />
      )}

      <PriceDialog
        open={priceDialog.open}
        onClose={priceDialog.closeDialog}
        priceData={priceData}
      />
      <AlertDialog
        variant="error"
        open={alertDialog.open}
        onClose={alertDialog.closeDialog}
        message={
          <span>
            <span style={{ display: 'block' }}>
              ¿Estás seguro de que deseas expirar este pedido?
            </span>
            <span style={{ display: 'block' }}>Esta acción no se puede deshacer.</span>
          </span>
        }
        title="Expirar pedido"
        onConfirm={handleConfirmExpiring}
        confirmText="Expirar pedido"
      />
      <AlertDialog
        variant="error"
        open={alertRemoveCourier.open}
        onClose={alertRemoveCourier.closeDialog}
        message={
          <span>
            <span style={{ display: 'block' }}>
              ¿Estás seguro de que deseas quitar el envío nacional?
            </span>
            <span style={{ display: 'block' }}>Esta acción no se puede deshacer.</span>
          </span>
        }
        title="Quitar envío nacional"
        onConfirm={handleRemoveCourier}
        confirmText="Quitar envío nacional"
      />
      <ShopperShippingDialog open={shippingDialog.open} onClose={shippingDialog.closeDialog} />
    </Card>
  );
};

export default OrderCard;
